/**
 * MEP store (zustand): generation flow state per model, the active generated
 * system's resources (devices, zones, circuits, findings), and the finding
 * override workflow (optimistic, reverted on failure — same pattern as space
 * patches in ./store.ts).
 */
import { create } from "zustand";

import * as api from "../api/client";
import type {
  ApiFinding,
  Ats,
  ElectricalSettings,
  EmergencySystem,
  GroundingSchedule,
  EmergencyBranch,
  HvacSettings,
  MepAirflow,
  MepCircuit,
  MepDevice,
  MepDiscipline,
  MepDuct,
  MepFitting,
  MepSystem,
  MepTerminal,
  MepZone,
  VoltageDropStudy,
  MechanicalOutput,
} from "../api/types";
import { IDLE, runGenerateFlow, type GenerateState } from "./generateFlow";

export type MepLoadStatus = "idle" | "loading" | "ready" | "error";

/** The emergency & standby power slice of the raw electrical Output. All
 * `null`/empty when the opt-in emergency stage did not run (byte-stable). */
export interface EmergencyOutputSlice {
  system: EmergencySystem | null;
  branch: Record<string, EmergencyBranch>;
  ats: Ats[];
}

const EMPTY_EMERGENCY: EmergencyOutputSlice = { system: null, branch: {}, ats: [] };
const EMPTY_MECHANICAL: MechanicalOutput = { equipment: [], load_screening: [], boq: null };

export interface MepState {
  /** Generate-flow state keyed by model id. */
  flow: Record<string, GenerateState>;
  /** Pinned seed per model (re-used on Generate while pinned). */
  pinnedSeed: Record<string, number | null>;

  /** HVAC generation settings per model (sent with `discipline: "hvac"`). */
  hvacSettings: Record<string, HvacSettings>;
  /** Electrical generation settings per model (sent with `discipline: "electrical"`). */
  electricalSettings: Record<string, ElectricalSettings>;

  /** Active system per model (latest generated or selected). */
  activeSystem: Record<string, MepSystem | null>;
  devices: Record<string, MepDevice[]>;
  zones: Record<string, MepZone[]>;
  circuits: Record<string, MepCircuit[]>;
  findings: Record<string, ApiFinding[]>;
  /** HVAC resources (empty arrays for fire-alarm systems). */
  ducts: Record<string, MepDuct[]>;
  fittings: Record<string, MepFitting[]>;
  terminals: Record<string, MepTerminal[]>;
  airflows: Record<string, MepAirflow[]>;
  /** Electrical rollup (raw JSON; empty for non-electrical systems). */
  electricalLoadCalc: Record<string, unknown>;
  electricalPanelSchedule: Record<string, unknown>;
  /** Grounding schedule from the raw electrical Output; `null` when the opt-in
   * grounding stage did not run (or a non-electrical system). */
  electricalGrounding: Record<string, GroundingSchedule | null>;
  /** Emergency & standby power slice from the raw electrical Output; all
   * `null`/empty when the opt-in emergency stage did not run. */
  electricalEmergency: Record<string, EmergencyOutputSlice>;
  /** Voltage-drop study from the raw electrical Output; `null` when the opt-in
   * voltage-drop stage did not run (or a non-electrical system). */
  electricalVoltageDrop: Record<string, VoltageDropStudy | null>;
  /** Mechanical slice from the raw mechanical Output (equipment + per-space
   * load screening + BoQ); all empty/null for non-mechanical systems. */
  mechanicalOutput: Record<string, MechanicalOutput>;
  resourcesStatus: Record<string, MepLoadStatus>;

  /** Last override failure message, cleared on the next attempt. */
  overrideError: string | null;

  /** Pin/unpin the seed used by the next Generate for this model. */
  setPinnedSeed: (modelId: string, seed: number | null) => void;
  /** Merge a partial HVAC settings update for this model. */
  setHvacSettings: (modelId: string, patch: HvacSettings) => void;
  /** Merge a partial electrical settings update for this model. */
  setElectricalSettings: (modelId: string, patch: ElectricalSettings) => void;
  /**
   * Run a generation for the model; follows job progress over SSE and loads
   * the system's resources on completion. HVAC generations send the model's
   * stored settings.
   */
  generate: (modelId: string, discipline: MepDiscipline) => Promise<void>;
  /** Load (or reload) all resources of a generated system. */
  loadSystem: (modelId: string, systemId: string) => Promise<void>;
  /** Override a finding with a reason (optimistic). Returns success. */
  override: (modelId: string, findingId: string, reason: string) => Promise<boolean>;
  /** Remove a finding override (optimistic). Returns success. */
  clearOverride: (modelId: string, findingId: string) => Promise<boolean>;
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Replace one finding row, immutably. */
export function replaceFinding(
  rows: ApiFinding[] | undefined,
  findingId: string,
  patch: Partial<ApiFinding>,
): ApiFinding[] {
  return (rows ?? []).map((f) => (f.id === findingId ? { ...f, ...patch } : f));
}

export const useMepStore = create<MepState>((set, get) => ({
  flow: {},
  pinnedSeed: {},
  hvacSettings: {},
  electricalSettings: {},
  activeSystem: {},
  devices: {},
  zones: {},
  circuits: {},
  findings: {},
  ducts: {},
  fittings: {},
  terminals: {},
  airflows: {},
  electricalLoadCalc: {},
  electricalPanelSchedule: {},
  electricalGrounding: {},
  electricalEmergency: {},
  electricalVoltageDrop: {},
  mechanicalOutput: {},
  resourcesStatus: {},
  overrideError: null,

  setPinnedSeed: (modelId, seed) => {
    set((state) => ({ pinnedSeed: { ...state.pinnedSeed, [modelId]: seed } }));
  },

  setHvacSettings: (modelId, patch) => {
    set((state) => ({
      hvacSettings: {
        ...state.hvacSettings,
        [modelId]: { ...state.hvacSettings[modelId], ...patch },
      },
    }));
  },

  setElectricalSettings: (modelId, patch) => {
    set((state) => ({
      electricalSettings: {
        ...state.electricalSettings,
        [modelId]: { ...state.electricalSettings[modelId], ...patch },
      },
    }));
  },

  generate: async (modelId, discipline) => {
    const current = get().flow[modelId] ?? IDLE;
    if (current.phase === "submitting" || current.phase === "running") return;

    const pinned = get().pinnedSeed[modelId] ?? null;
    const rawSettings =
      discipline === "hvac"
        ? (get().hvacSettings[modelId] ?? null)
        : discipline === "electrical"
          ? (get().electricalSettings[modelId] ?? null)
          : null;
    // Drop keys whose value is undefined (a cleared field) so the request
    // carries only what the engineer actually set.
    const settings =
      rawSettings === null
        ? null
        : (Object.fromEntries(
            Object.entries(rawSettings).filter(([, v]) => v !== undefined),
          ) as Record<string, unknown>);
    const final = await runGenerateFlow({
      modelId,
      request: {
        discipline,
        // Seed rolled server-side when absent (procedural variation).
        ...(pinned !== null ? { seed: pinned } : {}),
        ...(settings !== null && Object.keys(settings).length > 0 ? { settings } : {}),
      },
      onState: (flowState) => {
        set((state) => ({ flow: { ...state.flow, [modelId]: flowState } }));
      },
    });

    if (final.phase === "done") {
      await get().loadSystem(modelId, final.mepSystemId);
    }
  },

  loadSystem: async (modelId, systemId) => {
    set((state) => ({
      resourcesStatus: { ...state.resourcesStatus, [modelId]: "loading" },
    }));
    try {
      const system = await api.getMepSystem(systemId);
      const hvac = system.discipline === "hvac";
      const electrical = system.discipline === "electrical";
      // `fire` is the FALLBACK bucket, so every non-fire discipline must be
      // excluded explicitly — otherwise a non-fire system would fetch the
      // fire-alarm-only device/zone/circuit routes and 404 its whole load.
      const fire = !hvac && !electrical;
      // Devices/zones/circuits are fire-alarm-only routes (pinned contract);
      // ducts/fittings/terminals/airflows are HVAC-only; load-calc + panel
      // schedule are electrical-only; the mechanical slice reads the generic
      // /output route. Findings are shared across disciplines.
      const [
        devices,
        zones,
        circuits,
        ducts,
        fittings,
        terminals,
        airflows,
        findings,
        loadCalc,
        panelSchedule,
        electricalOutput,
        mechanicalOutput,
      ] = await Promise.all([
        fire ? api.getMepDevices(systemId) : Promise.resolve([] as MepDevice[]),
        fire ? api.getMepZones(systemId) : Promise.resolve([] as MepZone[]),
        fire ? api.getMepCircuits(systemId) : Promise.resolve([] as MepCircuit[]),
        hvac ? api.getMepDucts(systemId) : Promise.resolve([] as MepDuct[]),
        hvac ? api.getMepFittings(systemId) : Promise.resolve([] as MepFitting[]),
        hvac ? api.getMepTerminals(systemId) : Promise.resolve([] as MepTerminal[]),
        hvac ? api.getMepAirflows(systemId) : Promise.resolve([] as MepAirflow[]),
        api.getMepFindings(systemId),
        electrical ? api.getElectricalLoadCalc(systemId) : Promise.resolve(null),
        electrical ? api.getElectricalPanelSchedule(systemId) : Promise.resolve(null),
        // The opt-in grounding schedule + emergency slice both live on the raw
        // electrical Output. Fetched once; a failed/absent fetch degrades to null
        // (no grounding / emergency section), never aborting the load.
        electrical ? api.getElectricalOutput(systemId).catch(() => null) : Promise.resolve(null),
        // Mechanical is HVAC expanded, so the equipment/load-screening/BoQ
        // slice loads for an HVAC system — off the same raw Output route that
        // carries the ductwork. A failed/absent fetch degrades to the empty
        // slice (no equipment sections), never aborting the load.
        hvac
          ? api.getMechanicalOutput(systemId).catch(() => EMPTY_MECHANICAL)
          : Promise.resolve(EMPTY_MECHANICAL),
      ]);
      const grounding = electricalOutput?.grounding_schedule ?? null;
      const voltageDrop = electricalOutput?.voltage_drop_study ?? null;
      const emergency: EmergencyOutputSlice = electricalOutput
        ? {
            system: electricalOutput.emergency_system ?? null,
            branch: electricalOutput.emergency_branch ?? {},
            ats: electricalOutput.ats ?? [],
          }
        : EMPTY_EMERGENCY;
      set((state) => ({
        activeSystem: { ...state.activeSystem, [modelId]: system },
        devices: { ...state.devices, [modelId]: devices },
        zones: { ...state.zones, [modelId]: zones },
        circuits: { ...state.circuits, [modelId]: circuits },
        ducts: { ...state.ducts, [modelId]: ducts },
        fittings: { ...state.fittings, [modelId]: fittings },
        terminals: { ...state.terminals, [modelId]: terminals },
        airflows: { ...state.airflows, [modelId]: airflows },
        findings: { ...state.findings, [modelId]: findings },
        electricalLoadCalc: { ...state.electricalLoadCalc, [modelId]: loadCalc },
        electricalPanelSchedule: { ...state.electricalPanelSchedule, [modelId]: panelSchedule },
        electricalGrounding: { ...state.electricalGrounding, [modelId]: grounding },
        electricalEmergency: { ...state.electricalEmergency, [modelId]: emergency },
        electricalVoltageDrop: { ...state.electricalVoltageDrop, [modelId]: voltageDrop },
        mechanicalOutput: { ...state.mechanicalOutput, [modelId]: mechanicalOutput },
        resourcesStatus: { ...state.resourcesStatus, [modelId]: "ready" },
      }));
    } catch {
      set((state) => ({
        resourcesStatus: { ...state.resourcesStatus, [modelId]: "error" },
      }));
    }
  },

  override: async (modelId, findingId, reason) => {
    return applyOverrideOptimistically(
      modelId,
      findingId,
      {
        overridden: true,
        override_reason: reason,
      },
      () => api.overrideFinding(findingId, reason),
    );
  },

  clearOverride: async (modelId, findingId) => {
    return applyOverrideOptimistically(
      modelId,
      findingId,
      {
        overridden: false,
        override_reason: null,
      },
      () => api.clearFindingOverride(findingId),
    );
  },
}));

/**
 * Shared optimistic-update path for override/clearOverride: apply `patch` to
 * the finding row immediately, run the API call, and on failure revert ONLY
 * if the row still holds the optimistic value we set (compare-and-revert) —
 * a stale failure must never clobber a newer concurrent edit's state.
 */
async function applyOverrideOptimistically(
  modelId: string,
  findingId: string,
  patch: Pick<ApiFinding, "overridden" | "override_reason">,
  call: () => Promise<void>,
): Promise<boolean> {
  const { getState, setState } = useMepStore;
  const previous = getState().findings[modelId]?.find((f) => f.id === findingId);
  if (!previous) return false;

  // Keep the exact optimistic row object for the compare-and-revert check.
  const optimistic: ApiFinding = { ...previous, ...patch };
  setState((state) => ({
    overrideError: null,
    findings: {
      ...state.findings,
      [modelId]: (state.findings[modelId] ?? []).map((f) => (f.id === findingId ? optimistic : f)),
    },
  }));
  try {
    await call();
    return true;
  } catch (err) {
    setState((state) => {
      const rows = state.findings[modelId];
      const stillOurs = rows?.find((f) => f.id === findingId) === optimistic;
      return {
        overrideError: message(err),
        findings: stillOurs
          ? {
              ...state.findings,
              [modelId]: (rows ?? []).map((f) => (f.id === findingId ? previous : f)),
            }
          : state.findings,
      };
    });
    return false;
  }
}
